import { useLocation, useNavigate, useParams } from "react-router-dom";
import styles from "./index.module.css";
import { colorSeq, productData } from "../../../utils/constant";
import MultiselectDropdown from "../../elements/MultiselectDropdown";
import { useState, useEffect } from "react";
import DateRangePicker from "../../elements/DateRangePicker";
import LineChart from "../../elements/LineChart";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { fetchFnbStore } from "../../../store/slices/fnb/constants.slice";
import { fetchFnbPriceHistory } from "../../../store/slices/fnb/priceHistory.slice";
import { convert_usd_to_nzd } from "../../../utils/conversion";

// Function to generate unique colors for all locations
const getColorForIndex = (index) => {
  // If we have enough colors in colorSeq, use them
  if (index < colorSeq.length) {
    return colorSeq[index];
  }
  
  // Generate additional colors using HSL for better variety
  const hue = (index * 137.5) % 360; // Golden angle for good distribution
  const saturation = 70 + (index % 3) * 10; // Vary saturation
  const lightness = 45 + (index % 4) * 5; // Vary lightness
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

const prepareChartData = (
  priceHistoryData,
  selectedLocation = [],
  source,
  currency
) => {
  let lineGraphData = [];
  
  // Get all unique dates first
  let allDates = getLabels(priceHistoryData, selectedLocation);

  selectedLocation.forEach((location, index) => {
    if (priceHistoryData[location]) {
      // Get the first product from this location (since all products have the same structure)
      const productName = Object.keys(priceHistoryData[location])[0];
      const productData = priceHistoryData[location][productName];
      
      let temp = {
        label: location,
        data: [],
        borderColor: getColorForIndex(index),
        backgroundColor: getColorForIndex(index),
        borderWidth: 2,
        tension: 0.4,
        pointBackgroundColor: getColorForIndex(index),
        pointBorderColor: "#fff",
        pointRadius: 5,
      };

      // Create a map of date to price for this product
      const priceMap = new Map();
      productData.forEach((prod) => {
        const date = new Date(prod.price_date).toLocaleDateString("en-US");
        priceMap.set(date, prod.product_price);
      });

      // Fill in prices for all dates
      let lastPrice = null;
      allDates.forEach((date) => {
        if (priceMap.has(date)) {
          lastPrice = priceMap.get(date);
        }
        temp.data.push(lastPrice);
      });

      lineGraphData.push(temp);
    }
  });

  return lineGraphData;
};

const getLabels = (priceHistoryData, selectedLocation = []) => {
  let dates = new Set();

  // Collect all dates from all selected locations
  selectedLocation.forEach((location) => {
    if (priceHistoryData[location]) {
      const productName = Object.keys(priceHistoryData[location])[0];
      const productData = priceHistoryData[location][productName];
      
      productData.forEach((prod) => {
        let date = new Date(prod.price_date).toLocaleDateString("en-US");
        dates.add(date);
      });
    }
  });

  // Convert to array and sort
  const sortedDates = Array.from(dates).sort(
    (a, b) => new Date(a) - new Date(b)
  );
  return sortedDates;
};

const FnbPriceHistory = (props) => {
  const dispatch = useDispatch();
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const {
    data: { store },
    loading: constantLoading,
  } = useSelector((store) => store.fnbConstants);
  const { data, loading } = useSelector((store) => store.fnbPriceHistory);
  const { source, currency } = useSelector((store) => store.constant);
  const [formatData, setFormatData] = useState({});

  const params = useParams();
  const navigate = useNavigate();
  const productMeta = useLocation().state;

  useEffect(() => {
    if (!store.length) {
      dispatch(fetchFnbStore());
    }
    dispatch(fetchFnbPriceHistory(params.id));
  }, []);

  useEffect(() => {
    if (Object.keys(data).length) {
      // Get available locations from API data
      const locationsFromData = Object.keys(data);
      setAvailableLocations(locationsFromData);
      // Auto-select all available locations when data is loaded
      setSelectedLocation(locationsFromData);
    }
  }, [data]);

  const onBack = () => {
    navigate(-1);
  };

  const onSelectedLocationChange = (value) => {
    setSelectedLocation(value);
  };
  return (
    <div className={styles["container"]}>
      <div className={styles["sidebar"]}>
        <div className={styles["logo"]}>
          <img src="/logo_white.png" alt="logo white" />
        </div>
        <div className={styles["sidebar_content"]}>
          <div>
            <div>{productMeta.store}</div>
          </div>
          <div>{productMeta.title}</div>
          <div>{productMeta.price}</div>
        </div>
        <div className={styles["back"]} onClick={onBack}>
          <div>
            <i class="bi bi-caret-left-square"></i>
            BACK
          </div>
        </div>
      </div>
      <div className={styles["main"]}>
        <div className={styles["notification_strip"]}>
          <div className={styles["notification_content"]}>
            <i className="bi bi-exclamation-triangle"></i>
            <span>Some Data and features might show incorrectly for FNB as some features are being deployed, apologies for inconvenience</span>
          </div>
        </div>
        <div className={styles["head"]}>
          <div>Price History</div>
          <div className={styles["filters"]}>
            <div className={styles["location_filter"]}>
              <div>
                Location
                <i class="bi bi-pin"></i>
              </div>
              <div>
                {availableLocations.length > 0 ? (
                  <MultiselectDropdown
                    steps={availableLocations}
                    name="locations"
                    onChange={onSelectedLocationChange}
                  />
                ) : (
                  <div className={styles["no-data-message"]}>
                    {loading ? "Loading locations..." : "No location data available"}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <hr />
        <div className={styles["line-chart"]}>
          {selectedLocation.length > 0 && (
            <LineChart
              data={prepareChartData(data, selectedLocation, source, currency)}
              labels={getLabels(data, selectedLocation)}
            />
          )}
        </div>
        <hr />
        <div className={styles["identifiers"]}>
          <div className={styles["ihead"]}>Identifiers</div>
          <div className={styles["values"]}>
            <div>
              {selectedLocation?.map((location, index) => (
                <div
                  className={styles["value_box"]}
                  style={{
                    color: getColorForIndex(index),
                    border: `1px solid ${getColorForIndex(index)}`,
                  }}
                >
                  {location}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FnbPriceHistory;
